import { GroupAlignmentPolicy, GroupCompactionPolicy, HierarchicLayout, HierarchicLayoutBusDescriptor, HierarchicLayoutData, HierarchicLayoutEdgeRoutingStyle, HierarchicLayoutLayeringStrategy, HierarchicLayoutRoutingStyle, IArrow, NodeLabelMode, PolylineEdgeStyle, RecursiveEdgeStyle } from "yfiles";
import LayoutConfiguration from "./layoutConfiguration";
class HierarchicLayoutConfig extends LayoutConfiguration {
    createConfiguredLayout() {
        const layout = new HierarchicLayout({
            orthogonalRouting: true,
            fromScratchLayeringStrategy: HierarchicLayoutLayeringStrategy.HIERARCHICAL_OPTIMAL,
            integratedEdgeLabeling: false,
            componentLayoutEnabled: false,
            subgraphLayoutEnabled: false,
            minimumLayerDistance: 40.0,
            nodeToEdgeDistance: 20.0,
            nodeToNodeDistance: 30.0,
            edgeToEdgeDistance: 15.0,
            automaticEdgeGrouping: false,
            hideGroupsStageEnabled: false,
            considerNodeLabels: false,
            labelingEnabled: false,
            groupAlignmentPolicy: GroupAlignmentPolicy.CENTER,
            compactGroups: false,
            recursiveGroupLayering: false,
            backLoopRouting: false,
            backLoopRoutingForSelfLoops: false,
            maximumDuration: 5 * 1000
        });
        const nodePlacer = layout.nodePlacer;
        nodePlacer.barycenterMode = true;
        nodePlacer.horizontalCompaction = true;
        nodePlacer.nodeCompaction = false;
        nodePlacer.straightenEdges = true;
        nodePlacer.groupCompactionStrategy = GroupCompactionPolicy.NONE;
        layout.nodeLayoutDescriptor.nodeLabelMode = NodeLabelMode.NEVER;
        layout.nodeLayoutDescriptor.minimumDistance = Math.min(layout.nodeToNodeDistance, layout.nodeToEdgeDistance);
        layout.nodeLayoutDescriptor.minimumLayerHeight = 40;
        layout.nodeLayoutDescriptor.layerAlignment = 0.5;
        layout.edgeLayoutDescriptor.routingStyle = new HierarchicLayoutRoutingStyle(HierarchicLayoutEdgeRoutingStyle.ORTHOGONAL);
        layout.edgeLayoutDescriptor.routingStyle.curveShortcuts = true;
        layout.edgeLayoutDescriptor.routingStyle.curveUTurnSymmetry = 0;
        layout.edgeLayoutDescriptor.minimumFirstSegmentLength = 10.0;
        layout.edgeLayoutDescriptor.minimumLastSegmentLength = 15.0;
        layout.edgeLayoutDescriptor.minimumDistance = 15.0;
        layout.edgeLayoutDescriptor.minimumLength = 20.0;
        layout.edgeLayoutDescriptor.minimumSlope = 0.25;
        layout.edgeLayoutDescriptor.sourcePortOptimization = false;
        layout.edgeLayoutDescriptor.targetPortOptimization = false;
        layout.edgeLayoutDescriptor.recursiveEdgeStyle = RecursiveEdgeStyle.OFF;
        return layout;
    }
    createConfiguredLayoutData(graphComponent) {
        const { graph } = graphComponent;
        const layoutData = new HierarchicLayoutData();
        layoutData.edgeDirectedness.delegate = edge => {
            const { style } = edge;
            if (style instanceof PolylineEdgeStyle &&
                style.targetArrow &&
                style.targetArrow !== IArrow.NONE) {
                return 1;
            }
            return 0;
        };
        layoutData.edgeThickness.delegate = edge => {
            var _a;
            const { style } = edge;
            if (style instanceof PolylineEdgeStyle) {
                return ((_a = style.stroke) === null || _a === void 0 ? void 0 : _a.thickness) || 0;
            }
            return 0;
        };
        const allBusNodes = new Set();
        graph.nodes.forEach((node) => {
            if (!graph.isGroupNode(node) && !allBusNodes.has(node)) {
                if (graph.inDegree(node) >= 4) {
                    const busDescriptor = new HierarchicLayoutBusDescriptor();
                    const busEdges = this.getBusEdges(graph, node, allBusNodes, graph.inEdgesAt(node));
                    if (busEdges.length > 0) {
                        layoutData.buses.add(busDescriptor).items = busEdges;
                    }
                }
                if (graph.outDegree(node) >= 4) {
                    const busDescriptor = new HierarchicLayoutBusDescriptor();
                    const busEdges = this.getBusEdges(graph, node, allBusNodes, graph.outEdgesAt(node));
                    if (busEdges.length > 0) {
                        layoutData.buses.add(busDescriptor).items = busEdges;
                    }
                }
            }
        });
        return layoutData;
    }
    getBusEdges(graph, node, allBusNodes, edges) {
        if (graph.isGroupNode(node)) {
            return [];
        }
        const busNodes = new Set();
        let interEdgeCount = 0;
        const busEdges = [];
        const busNodesWithOtherEdges = [];
        const node2BusEdge = new Map();
        for (const edge of edges) {
            const other = edge.opposite(node);
            if (!busNodes.has(other)) {
                busNodes.add(other);
                busEdges.push(edge);
                node2BusEdge.set(other, edge);
                const otherEdgesCount = graph.degree(other) - 1;
                if (otherEdgesCount > 0) {
                    busNodesWithOtherEdges.push(other);
                    interEdgeCount += otherEdgesCount;
                }
            }
        }
        const finalBusEdges = [];
        const removedBusEdges = new Set();
        let busSize = busNodes.size;
        while (busSize >= 4) {
            if (interEdgeCount <= busSize * 0.25) {
                for (const edge of busEdges) {
                    if (!removedBusEdges.has(edge)) {
                        finalBusEdges.push(edge);
                        allBusNodes.add(edge.opposite(node));
                    }
                }
                break;
            }
            else {
                if (busNodesWithOtherEdges.length > 0) {
                    const nodeToRemove = busNodesWithOtherEdges.pop();
                    if (nodeToRemove) {
                        removedBusEdges.add(node2BusEdge.get(nodeToRemove));
                        busSize--;
                        interEdgeCount -= graph.degree(nodeToRemove) - 1;
                    }
                }
            }
        }
        return finalBusEdges;
    }
}
export default HierarchicLayoutConfig;
